import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Alert,
  Paper,
  Typography,
  Divider,
  MobileStepper,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { saveElevationData } from '../../utils/elevationStorage';

import RaceDetailsSection from './RaceDetailsSection';
import NutritionSection from './NutritionSection';
import AidStationsSection from './AidStationsSection';
import StartItemsSection from './StartItemsSection';
import { getPreRaceFuelState } from '../../utils/preRaceFuel';

// Define the steps
const steps = [
  { label: 'Race Details', description: 'Basic race information and course details' },
  { label: 'Nutrition Plan', description: 'Set your nutrition strategy and fuel sources' },
  { label: 'Aid Stations', description: 'Plan your aid stations and drop bags' },
  { label: 'Start Items', description: 'What to carry at the start of the race' },
  { label: 'Review', description: 'Review your race plan before generating' }
];

// Custom styled stepper connector with progress line
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.divider,
    borderRadius: 1,
  },
}));

// Custom step icon component
const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 4px 10px 0 rgba(44, 95, 79, 0.25)',
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.primary.main,
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? (
        <CheckCircleIcon sx={{ fontSize: 28 }} />
      ) : (
        <Typography variant="body1" fontWeight="bold">
          {icon}
        </Typography>
      )}
    </ColorlibStepIconRoot>
  );
}

const RaceForm = ({ onSubmit, initialData, isEditing, raceName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [visitedSteps, setVisitedSteps] = useState({ 0: true });
  const [validationErrors, setValidationErrors] = useState({});
  const [generateButtonClicked, setGenerateButtonClicked] = useState(false);
  const [formData, setFormData] = useState({
    raceName: '',
    raceDate: '',
    distance: '',
    distanceUnit: 'miles',
    gpxData: null,
    elevationData: null,
    paceModel: 'even', // 'even' | 'terrain' | 'simulation' — terrain uses GPX elevation in splits
    cutoffTimeHours: '',
    cutoffTimeMinutes: '',
    startTimeHours: 6,
    startTimeMinutes: 0,
    startTimeAMPM: 'AM',
    nutritionType: 'gelFrequency', // 'gelFrequency' or 'carbsPerHour'
    gelFrequency: '',
    gelFrequencyUnit: 'miles',
    carbsPerHour: '',
    fuelSources: [
      { type: 'gel', name: 'Energy Gel', carbsPerUnit: 25, unitsPerHour: 0 }
    ],
    additionalFuelSources: [], // Additional fuel sources for gel frequency option
    preRaceFuelEnabled: false,
    preRaceFuelDescription: '',
    preRaceGelEquivalents: 1,
    goalTimeHours: '',
    goalTimeMinutes: '',
    aidStations: [], // Array of aid stations with { location, name, hasDropBag, items, hasPacer, pacerName, hasCrew, notes }
    startItems: [], // Items for Mile 0/Start
    aidStationUnit: 'miles' // Default unit for aid stations
  });

  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  // Effect to update form when initialData changes
  useEffect(() => {
    if (initialData) {
      // Convert decimal hours to hours and minutes
      const cutoffHours = Math.floor(initialData.cutoffTime);
      const cutoffMinutes = Math.round((initialData.cutoffTime - cutoffHours) * 60);
      
      let goalHours = '';
      let goalMinutes = '';
      if (initialData.goalTime) {
        goalHours = Math.floor(initialData.goalTime);
        goalMinutes = Math.round((initialData.goalTime - goalHours) * 60);
      }

      // Ensure aidStationUnit gets set from initialData or to the default value
      const aidStationUnit = initialData.aidStationUnit || 'miles';
      console.log("RaceForm: Setting initial aidStationUnit to", aidStationUnit);

      const next = {
        ...initialData,
        cutoffTimeHours: cutoffHours.toString(),
        cutoffTimeMinutes: cutoffMinutes.toString(),
        goalTimeHours: goalHours.toString(),
        goalTimeMinutes: goalMinutes.toString(),
        aidStationUnit: aidStationUnit // Ensure this is explicitly set
      };
      if (next.paceModel === undefined || next.paceModel === null || next.paceModel === '') {
        next.paceModel = 'even';
      }
      if (next.preRaceFuelEnabled === undefined && next.preRaceGel !== undefined) {
        next.preRaceFuelEnabled = !!next.preRaceGel;
      }
      if (next.preRaceGelEquivalents === undefined || next.preRaceGelEquivalents === '') {
        next.preRaceGelEquivalents = 1;
      }
      if (
        (next.preRaceFuelDescription === undefined || String(next.preRaceFuelDescription).trim() === '') &&
        next.preRaceGel
      ) {
        next.preRaceFuelDescription = 'Pre-race gel';
      }

      if (next.aidStations && Array.isArray(next.aidStations)) {
        next.aidStations = next.aidStations.map((s) => {
          if (s.isFinish) return s;
          const hasH =
            s.aidStopDurationHours !== undefined && s.aidStopDurationHours !== '';
          const hasM =
            s.aidStopDurationMinutes !== undefined && s.aidStopDurationMinutes !== '';
          if (hasH || hasM) {
            return {
              ...s,
              aidStopDurationHours: s.aidStopDurationHours ?? '',
              aidStopDurationMinutes: s.aidStopDurationMinutes ?? ''
            };
          }
          const legacy = parseFloat(s.stopTimeMinutes);
          if (!Number.isNaN(legacy) && legacy > 0) {
            const total = Math.round(legacy);
            return {
              ...s,
              aidStopDurationHours: String(Math.floor(total / 60)),
              aidStopDurationMinutes: String(total % 60)
            };
          }
          return {
            ...s,
            aidStopDurationHours: '',
            aidStopDurationMinutes: ''
          };
        });
      }

      setFormData(next);
    }
  }, [initialData]);

  // Add debug logging to track formData changes
  useEffect(() => {
    console.log("formData.aidStationUnit updated to:", formData.aidStationUnit);
  }, [formData.aidStationUnit]);

  // Effect to track when component unmounts
  useEffect(() => {
    // Set mountedRef.current to true when the component mounts
    mountedRef.current = true;

    // Cleanup function to set mountedRef.current to false when the component unmounts
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reference to track the last time elevationData was updated
  const lastElevationUpdateRef = useRef(0);

  const handleChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation errors for this field if they exist
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  const handleFuelSourceChange = (index, field, value, isAdditional = false) => {
    if (isAdditional) {
      const updatedFuelSources = [...formData.additionalFuelSources];
      updatedFuelSources[index] = {
        ...updatedFuelSources[index],
        [field]: value
      };
      setFormData({
        ...formData,
        additionalFuelSources: updatedFuelSources
      });
    } else {
      const updatedFuelSources = [...formData.fuelSources];
      updatedFuelSources[index] = {
        ...updatedFuelSources[index],
        [field]: value
      };
      setFormData({
        ...formData,
        fuelSources: updatedFuelSources
      });
    }
  };

  const handleAddFuelSource = (isAdditional = false) => {
    if (isAdditional) {
      setFormData({
        ...formData,
        additionalFuelSources: [
          ...formData.additionalFuelSources,
          { 
            type: 'drinkMix', 
            name: 'Drink Mix', 
            carbsPerUnit: 25, 
            frequency: formData.gelFrequency, 
            frequencyUnit: formData.gelFrequencyUnit,
            unitsPerFrequency: 1 
          }
        ]
      });
    } else {
      setFormData({
        ...formData,
        fuelSources: [
          ...formData.fuelSources,
          { type: 'gel', name: 'Energy Gel', carbsPerUnit: 25, unitsPerHour: 0 }
        ]
      });
    }
  };

  const handleRemoveFuelSource = (index, isAdditional = false) => {
    if (isAdditional) {
      const newFuelSources = [...formData.additionalFuelSources];
      newFuelSources.splice(index, 1);
      setFormData({
        ...formData,
        additionalFuelSources: newFuelSources
      });
    } else {
      if (formData.fuelSources.length <= 1) {
        return; // Don't allow removing the last fuel source
      }
      const newFuelSources = [...formData.fuelSources];
      newFuelSources.splice(index, 1);
      setFormData({
        ...formData,
        fuelSources: newFuelSources
      });
    }
  };

  // Helper function to sort aid stations by location
  const sortAidStations = (stations) => {
    // First separate stations with valid locations from those with empty or invalid locations
    const validStations = stations.filter(station => 
      station.location !== '' && !isNaN(parseFloat(station.location))
    );
    const invalidStations = stations.filter(station => 
      station.location === '' || isNaN(parseFloat(station.location))
    );
    
    // Sort valid stations by location
    const sortedValidStations = [...validStations].sort((a, b) => 
      parseFloat(a.location) - parseFloat(b.location)
    );
    
    // Return sorted valid stations followed by invalid ones
    return [...sortedValidStations, ...invalidStations];
  };

  const lastAidStationRef = useRef(null);

  const handleAddAidStation = (stationData = null) => {
    setFormData(prevFormData => {
      const defaultStation = { 
        location: '', 
        name: '', 
        hasDropBag: false, 
        items: [], 
        hasPacer: false, 
        pacerName: '', 
        hasCrew: false,
        notes: '',
        aidStopDurationHours: '',
        aidStopDurationMinutes: '',
        isFinish: false
      };

      const newStation = stationData || defaultStation;
      const updatedAidStations = [...prevFormData.aidStations, newStation];
      
      return {
        ...prevFormData,
        aidStations: updatedAidStations
      };
    });

    // Scroll to the new aid station after it's been added and rendered
    setTimeout(() => {
      if (lastAidStationRef.current) {
        lastAidStationRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const handleRemoveAidStation = (index) => {
    setFormData(prevFormData => {
      const newAidStations = [...prevFormData.aidStations];
      newAidStations.splice(index, 1);
      
      return {
        ...prevFormData,
        aidStations: newAidStations // No need to sort after removal
      };
    });
  };

  const handleAidStationChange = (index, field, value) => {
    console.log(`handleAidStationChange called with:`, { index, field, value });
    console.log(`handleAidStationChange types:`, { 
      indexType: typeof index, 
      fieldType: typeof field, 
      valueType: typeof value,
      value: value 
    });
    
    // Special case for aidStationUnit - it's directly a string field name
    if (index === 'aidStationUnit') {
      console.log(`RaceForm: Setting aidStationUnit directly to "${field}"`);
      console.log(`RaceForm: Type of field: ${typeof field}`);
      
      // The value parameter is actually passed as the field parameter
      let unitValue = field;
      
      if (field === 'kilometers') {
        console.log("RaceForm: Confirmed value is the string 'kilometers'");
      } else if (field === 'miles') {
        console.log("RaceForm: Confirmed value is the string 'miles'");
      } else {
        console.log(`RaceForm: Value is invalid: ${field}, defaulting to 'miles'`);
        unitValue = 'miles';
      }
      
      // Use functional state update to ensure we're working with the latest state
      setFormData(prevState => {
        const newState = { ...prevState, aidStationUnit: unitValue };
        console.log("New formData state will be:", newState);
        console.log("New aidStationUnit will be:", unitValue);
        return newState;
      });
      return;
    }
    
    // Handle other string field names (direct form fields)
    if (typeof index === 'string') {
      const fieldName = index;
      console.log(`RaceForm: Setting direct field ${fieldName} to`, value);
      
      // Update the form data with a functional update to avoid stale state issues
      setFormData(prevState => {
        const newState = {
          ...prevState,
          [fieldName]: value
        };
        console.log("New formData state will be:", newState);
        return newState;
      });
      return;
    }
    
    // Handle aid station field updates
    console.log(`RaceForm: Updating aid station ${index}.${field} to ${value}`);
    
    // Use functional updates to avoid stale state
    setFormData(prevState => {
      const updatedAidStations = [...prevState.aidStations];
      updatedAidStations[index] = {
        ...updatedAidStations[index],
        [field]: value
      };
      
      // Do not sort during typing
      return {
        ...prevState,
        aidStations: updatedAidStations
      };
    });
  };

  const handleAddItemToDropBag = (bagIndex, item) => {
    console.log(`[DEBUG] RaceForm adding item to bag ${bagIndex}: "${item}"`);
    
    setFormData(prevFormData => {
      // Get the current aid stations array
      const updatedAidStations = [...prevFormData.aidStations];
      
      // Get the current items array for this station
      const currentItems = updatedAidStations[bagIndex]?.items || [];
      
      // Create a new items array with the new item added
      const newItems = [...currentItems, item];
      
      console.log(`[DEBUG] Items before: ${currentItems.length}, after: ${newItems.length}`);
      
      // Update the aid station object
      updatedAidStations[bagIndex] = {
        ...updatedAidStations[bagIndex],
        items: newItems
      };
      
      // Return the updated form data
      return {
        ...prevFormData,
        aidStations: updatedAidStations
      };
    });
  };

  const handleRemoveItemFromDropBag = (bagIndex, itemIndex) => {
    const updatedAidStations = [...formData.aidStations];
    const items = [...updatedAidStations[bagIndex].items];
    items.splice(itemIndex, 1);
    updatedAidStations[bagIndex] = {
      ...updatedAidStations[bagIndex],
      items: items
    };
    setFormData({
      ...formData,
      aidStations: updatedAidStations
    });
  };

  const handleAddStartItem = (item) => {
    setFormData({
      ...formData,
      startItems: [...formData.startItems, item]
    });
  };

  const handleRemoveStartItem = (index) => {
    const newStartItems = [...formData.startItems];
    newStartItems.splice(index, 1);
    setFormData({
      ...formData,
      startItems: newStartItems
    });
  };

  const handleSubmit = (e, forceSubmit = false) => {
    if (e) e.preventDefault();
    
    // Prevent automatic form submission when on the Review page
    // Only process submission when the Generate button was clicked
    if (activeStep === steps.length - 1 && !generateButtonClicked && !forceSubmit) {
      return;
    }
    
    // Check for required fields
    const errors = {};
    if (!formData.raceName?.trim()) {
      errors.raceName = 'Please enter a race name';
    }

    if (!formData.distance) {
      errors.distance = 'Please enter a race distance';
    }

    // Process cutoff time
    const cutoffHours = parseInt(formData.cutoffTimeHours || 0, 10);
    const cutoffMinutes = parseInt(formData.cutoffTimeMinutes || 0, 10);
    
    // Process goal time
    const goalHours = parseInt(formData.goalTimeHours || 0, 10);
    const goalMinutes = parseInt(formData.goalTimeMinutes || 0, 10);
    
    // Log for troubleshooting
    console.log("Processing time values:", {
      cutoffTimeHours: formData.cutoffTimeHours,
      cutoffTimeMinutes: formData.cutoffTimeMinutes,
      goalTimeHours: formData.goalTimeHours,
      goalTimeMinutes: formData.goalTimeMinutes,
      parsedCutoffHours: cutoffHours,
      parsedCutoffMinutes: cutoffMinutes,
      parsedGoalHours: goalHours,
      parsedGoalMinutes: goalMinutes
    });
    
    // Always set the time values as numeric values (0 if not provided)
    // This ensures they're not undefined which can cause issues in calculations
    const cutoffTime = Number(cutoffHours) + (Number(cutoffMinutes) / 60);
    const goalTime = Number(goalHours) + (Number(goalMinutes) / 60);
      
    console.log("Computed time values:", { 
      cutoffTime, 
      goalTime, 
      cutoffTimeType: typeof cutoffTime, 
      goalTimeType: typeof goalTime
    });
    
    // Add additional validations if needed
    const additionalErrors = validateForm();
    Object.assign(errors, additionalErrors);
    
    // Set validation errors if any
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Please fix the highlighted errors before generating your race plan.');
      return;
    }

    // Clear validation errors
    setValidationErrors({});
    
    // Convert aid station locations to numbers and preserve unit information
    const processedAidStations = formData.aidStations.map(station => ({
      ...station,
      location: parseFloat(station.location),
      originalLocation: station.originalLocation !== undefined ? parseFloat(station.originalLocation) : parseFloat(station.location),
      originalUnit: station.originalUnit || formData.aidStationUnit || 'miles'
    }));
    
    console.log("Submitting form with aid station unit:", formData.aidStationUnit);
    console.log("Processed aid stations:", processedAidStations);
    console.log("Submitting with time values:", { cutoffTime, goalTime });

    // Submit form with all the data
    onSubmit({
      ...formData,
      aidStations: processedAidStations,
      cutoffTime,
      goalTime,
      // Ensure distance is a number
      distance: parseFloat(formData.distance)
    });
  };

  // Add this helper function above handleGPXUpload
  const calculateElevationGain = (elevationData) => {
    if (!elevationData || !Array.isArray(elevationData) || elevationData.length < 2) {
      return 0;
    }
    
    let totalGain = 0;
    for (let i = 1; i < elevationData.length; i++) {
      const currentElevation = elevationData[i].elevation;
      const previousElevation = elevationData[i-1].elevation;
      const diff = currentElevation - previousElevation;
      if (diff > 0) {
        totalGain += diff;
      }
    }
    
    return Math.round(totalGain);
  };

  const handleGPXUpload = (gpxData, elevationData, distance) => {
    console.log("handleGPXUpload called with:", {
      gpxDataLength: gpxData ? gpxData.length : 0,
      elevationDataLength: elevationData ? elevationData.length : 0,
      distance
    });
    
    // Record this update attempt
    const currentUpdateId = Date.now();
    lastElevationUpdateRef.current = currentUpdateId;
    
    // Sample logging to inspect data structure
    if (elevationData && elevationData.length > 0) {
      console.log("Sample elevation data point:", elevationData[0]);
    }
    
    // Validate input data
    if (!elevationData || !Array.isArray(elevationData) || elevationData.length === 0) {
      console.error("Invalid or empty elevation data received");
      return;
    }
    
    // Ensure all elevation points have required properties and convert to consistent format
    const processedElevationData = elevationData.map(point => {
      // Ensure all properties are numbers
      const distance = typeof point.distance === 'number' ? point.distance : parseFloat(point.distance) || 0;
      let elevation = typeof point.elevation === 'number' ? point.elevation : parseFloat(point.elevation) || 0;
      
      // Convert elevation to meters if it appears to be in feet (most GPS devices use meters)
      // This is a heuristic - if the elevation is very large, it's likely in feet
      if (elevation > 5000) {
        elevation = elevation / 3.28084; // Convert feet to meters
      }
      
      // If point already has cumulativeDistance, ensure it's a number
      if (point.cumulativeDistance !== undefined) {
        return {
          ...point,
          distance,
          elevation,
          cumulativeDistance: typeof point.cumulativeDistance === 'number' ? 
            point.cumulativeDistance : parseFloat(point.cumulativeDistance) || 0
        };
      }
      
      // GPX `distance` is cumulative miles along the track — always convert to meters
      return {
        ...point,
        distance,
        elevation,
        cumulativeDistance: distance * 1609.34
      };
    });
    
    console.log(`Processed ${processedElevationData.length} elevation points with cumulativeDistance`);
    console.log("Sample processed elevation point:", processedElevationData[0]);
    
    // Create a clean copy of the elevation data to avoid reference issues
    const finalElevationData = JSON.parse(JSON.stringify(processedElevationData));
    
    // Store using dual storage utility (sessionStorage + localStorage backup)
    try {
      console.log("RaceForm: Storing elevation data, length:", finalElevationData.length);
      saveElevationData(finalElevationData, formData.id);
      console.log("RaceForm: Successfully stored elevation data");
    } catch (error) {
      console.error("RaceForm: Error storing elevation data:", error);
    }
    
    // Update the form data with the new information
    setTimeout(() => {
      setFormData({
        ...formData,
        distance: distance ? parseFloat(distance).toFixed(1) : formData.distance,
        elevationData: finalElevationData,
        elevationGain: calculateElevationGain(finalElevationData)
      });
      console.log("Form data updated with elevation data, length:", finalElevationData.length);
    }, 10); // Small delay to ensure state update is complete
  };

  // Validate the current step
  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // Race Details
        if (!formData.distance || isNaN(parseFloat(formData.distance)) || parseFloat(formData.distance) <= 0) {
          errors.distance = 'Please enter a valid race distance';
        }
        if (!formData.cutoffTimeHours && !formData.cutoffTimeMinutes) {
          errors.cutoffTime = 'Please enter a cutoff time';
        }
        break;
      case 1: // Nutrition
        if (formData.nutritionType === 'gelFrequency' && 
            (!formData.gelFrequency || isNaN(parseFloat(formData.gelFrequency)) || parseFloat(formData.gelFrequency) <= 0)) {
          errors.gelFrequency = 'Please enter a valid gel frequency';
        }
        if (formData.nutritionType === 'carbsPerHour' && 
            (!formData.carbsPerHour || isNaN(parseFloat(formData.carbsPerHour)) || parseFloat(formData.carbsPerHour) <= 0)) {
          errors.carbsPerHour = 'Please enter a valid carbs per hour value';
        }
        break;
      case 2: // Aid Stations
        formData.aidStations.forEach((station, index) => {
          if (!station.location || isNaN(parseFloat(station.location)) || parseFloat(station.location) <= 0) {
            errors[`aidStation_${index}`] = 'Please enter a valid location';
          }
        });
        break;
      default:
        break;
    }
    
    return errors;
  };

  // Validate the entire form
  const validateForm = () => {
    let allErrors = {};
    
    // Validate each step
    for (let i = 0; i < steps.length - 1; i++) {
      const stepErrors = validateStep(i);
      allErrors = { ...allErrors, ...stepErrors };
    }
    
    return allErrors;
  };

  // Check if a step is completable
  const isStepCompletable = (step) => {
    const errors = validateStep(step);
    return Object.keys(errors).length === 0;
  };

  // Handle direct navigation to a step
  const handleStepClick = (stepIndex) => {
    // Reset the generate button clicked flag
    setGenerateButtonClicked(false);
    
    // Don't do anything if clicking the current step
    if (stepIndex === activeStep) return;
    
    // If trying to navigate forward to a step we haven't visited yet
    if (stepIndex > activeStep && !visitedSteps[stepIndex]) {
      // Validate all steps up to the target step
      for (let i = activeStep; i < stepIndex; i++) {
        if (!completedSteps[i]) {
          const errors = validateStep(i);
          
          if (Object.keys(errors).length > 0) {
            setValidationErrors({...validationErrors, ...errors});
            setError(`Please complete step ${i + 1} (${steps[i].label}) before proceeding.`);
            return;
          }
          
          // Mark the step as completed
          setCompletedSteps(prev => ({
            ...prev,
            [i]: true
          }));
        }
      }
    }
    
    // If we're navigating to a step we've already visited, allow it
    if (visitedSteps[stepIndex]) {
      // Clear any existing errors when changing steps
      setError('');
      setActiveStep(stepIndex);
      
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    
    // Mark this step as visited
    setVisitedSteps(prev => ({
      ...prev,
      [stepIndex]: true
    }));
    
    // Clear any existing errors when changing steps
    setError('');
    setActiveStep(stepIndex);
    
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle moving to the next step
  const handleNext = () => {
    // Reset the generate button clicked flag
    setGenerateButtonClicked(false);
    
    const errors = validateStep(activeStep);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors({...validationErrors, ...errors});
      setError('Please fix the highlighted errors before proceeding.');
      return;
    }
    
    // If leaving the aid stations step, sort the aid stations
    if (activeStep === 2) {
      setFormData(prevState => {
        return {
          ...prevState,
          aidStations: sortAidStations(prevState.aidStations)
        };
      });
    }
    
    // Mark the current step as completed
    setCompletedSteps(prev => ({
      ...prev,
      [activeStep]: true
    }));
    
    // Mark the next step as visited
    setVisitedSteps(prev => ({
      ...prev,
      [activeStep + 1]: true
    }));
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError('');
    
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle moving to the previous step
  const handleBack = () => {
    // Reset the generate button clicked flag
    setGenerateButtonClicked(false);
    
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  // Determine if a step is accessible
  const isStepAccessible = (index) => {
    // Always allow access to steps we've already visited
    if (visitedSteps[index]) return true;
    
    // For unvisited steps, only allow if all previous steps are complete
    for (let i = 0; i < index; i++) {
      if (!completedSteps[i]) return false;
    }
    
    return true;
  };

  // Render the current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <RaceDetailsSection 
            formData={formData} 
            onGPXUpload={handleGPXUpload}
            onChange={handleChange}
            errors={validationErrors} 
          />
        );
      case 1:
        return (
          <NutritionSection 
            formData={formData} 
            onChange={handleChange}
            onFuelSourceChange={handleFuelSourceChange}
            onAddFuelSource={handleAddFuelSource}
            onRemoveFuelSource={handleRemoveFuelSource}
            errors={validationErrors}
          />
        );
      case 2:
        return (
          <AidStationsSection
            aidStations={formData.aidStations}
            onAddAidStation={handleAddAidStation}
            onAddFinish={handleAddFinish}
            onRemoveAidStation={handleRemoveAidStation}
            onAidStationChange={handleAidStationChange}
            onAddItemToDropBag={handleAddItemToDropBag}
            onRemoveItemFromDropBag={handleRemoveItemFromDropBag}
            onAddItemsToDropBag={handleAddItemsToDropBag}
            onRemoveItemsFromDropBag={handleRemoveItemsFromDropBag}
            lastAidStationRef={lastAidStationRef}
            unit={formData.aidStationUnit}
            distance={formData.distance}
            startTimeHours={formData.startTimeHours}
            startTimeMinutes={formData.startTimeMinutes}
            startTimeAMPM={formData.startTimeAMPM}
          />
        );
      case 3:
        return (
          <StartItemsSection 
            startItems={formData.startItems}
            onAddStartItem={handleAddStartItem}
            onAddMultipleItems={handleAddMultipleStartItems}
            onRemoveStartItem={handleRemoveStartItem}
          />
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review Your Race Plan</Typography>
            <Typography paragraph>
              You're all set! Review the information below and click "Generate Race Plan" when you're ready.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">{formData.raceName || 'Unnamed Race'}</Typography>
              <Typography>
                {formData.distance} {formData.distanceUnit}
                {formData.elevationData && ' with elevation data'}
              </Typography>
              <Typography>
                Start time: {formData.startTimeHours}:{formData.startTimeMinutes.toString().padStart(2, '0')} {formData.startTimeAMPM}
              </Typography>
              <Typography>
                Cutoff time: {formData.cutoffTimeHours}h {formData.cutoffTimeMinutes}m
              </Typography>
              {(formData.goalTimeHours || formData.goalTimeMinutes) && (
                <Typography>
                  Goal time: {formData.goalTimeHours}h {formData.goalTimeMinutes}m
                </Typography>
              )}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Nutrition Strategy</Typography>
              {formData.nutritionType === 'gelFrequency' ? (
                <Typography>
                  Gel every {formData.gelFrequency} {formData.gelFrequencyUnit}
                  {(() => {
                    const pr = getPreRaceFuelState(formData);
                    if (!pr.enabled) return null;
                    return `, pre-race: ${pr.description}${pr.gelEquivalents > 0 ? ` (${pr.gelEquivalents} gel equiv.)` : ''}`;
                  })()}
                  {formData.additionalFuelSources.length > 0 && `, plus ${formData.additionalFuelSources.length} additional fuel sources`}
                </Typography>
              ) : (
                <Typography>
                  {formData.carbsPerHour} carbs per hour with {formData.fuelSources.length} fuel sources
                  {(() => {
                    const pr = getPreRaceFuelState(formData);
                    if (!pr.enabled) return null;
                    return `, pre-race: ${pr.description}${pr.gelEquivalents > 0 ? ` (${pr.gelEquivalents} gel equiv.)` : ''}`;
                  })()}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Aid Stations & Drop Bags</Typography>
              <Typography>
                {formData.aidStations.length} aid stations set up
                {formData.aidStations.filter(s => s.hasDropBag).length > 0 && 
                  `, with ${formData.aidStations.filter(s => s.hasDropBag).length} drop bags`}
              </Typography>
              {formData.aidStations.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Sorted by distance: {formData.aidStations.map(s => s.location).join(', ')} {formData.aidStationUnit}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Start Items</Typography>
              <Typography>
                {formData.startItems.length > 0 
                  ? `${formData.startItems.length} items to carry at the start`
                  : 'No start items specified'}
              </Typography>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  // For monitoring purposes in dev tools
  useEffect(() => {
    console.log('Completed steps:', completedSteps);
    console.log('Visited steps:', visitedSteps);
  }, [completedSteps, visitedSteps]);

  // Monitor elevationData changes
  useEffect(() => {
    console.log('ELEVATION DATA CHANGE DETECTED:', {
      exists: !!formData.elevationData,
      length: formData.elevationData ? formData.elevationData.length : 0,
      sample: formData.elevationData && formData.elevationData.length > 0 ? 
        formData.elevationData[0] : null,
      formDataKeys: Object.keys(formData)
    });
  }, [formData, formData.elevationData]);

  // Add multiple items to a drop bag in a batch operation
  const handleAddItemsToDropBag = (bagIndex, itemsArray) => {
    if (!itemsArray || itemsArray.length === 0) return;
    
    console.log(`[DEBUG] RaceForm adding ${itemsArray.length} items to bag ${bagIndex}:`, itemsArray);
    
    setFormData(prevFormData => {
      // Get the current aid stations array
      const updatedAidStations = [...prevFormData.aidStations];
      
      // Get the current items array for this station
      const currentItems = updatedAidStations[bagIndex]?.items || [];
      
      // Create a new items array with all new items added
      const newItems = [...currentItems, ...itemsArray];
      
      console.log(`[DEBUG] Items before: ${currentItems.length}, after: ${newItems.length}`);
      
      // Update the aid station object
      updatedAidStations[bagIndex] = {
        ...updatedAidStations[bagIndex],
        items: newItems
      };
      
      // Return the updated form data (no need to sort here as we're not changing locations)
      return {
        ...prevFormData,
        aidStations: updatedAidStations
      };
    });
  };

  // Add multiple start items at once
  const handleAddMultipleStartItems = (itemsArray) => {
    console.log(`[DEBUG] Adding ${itemsArray.length} start items: ${JSON.stringify(itemsArray)}`);
    
    setFormData(prevFormData => {
      // Get the current start items array
      const currentItems = prevFormData.startItems || [];
      
      // Create a new items array with all new items added
      const newItems = [...currentItems, ...itemsArray];
      
      console.log(`[DEBUG] Batch start items before: ${currentItems.length}, after: ${newItems.length}`);
      
      // Return the updated form data
      return {
        ...prevFormData,
        startItems: newItems
      };
    });
  };

  // Remove multiple items from a drop bag at once
  const handleRemoveItemsFromDropBag = (bagIndex, itemIndices) => {
    if (!itemIndices || itemIndices.length === 0) return;
    
    console.log(`[DEBUG] RaceForm removing ${itemIndices.length} items from bag ${bagIndex}:`, itemIndices);
    
    setFormData(prevFormData => {
      // Get the current aid stations array
      const updatedAidStations = [...prevFormData.aidStations];
      
      // Get the current items array for this station
      const currentItems = updatedAidStations[bagIndex]?.items || [];
      
      // Create a new items array with the specified items removed
      const newItems = currentItems.filter((_, index) => !itemIndices.includes(index));
      
      console.log(`[DEBUG] Items before: ${currentItems.length}, after: ${newItems.length}`);
      
      // Update the aid station object
      updatedAidStations[bagIndex] = {
        ...updatedAidStations[bagIndex],
        items: newItems
      };
      
      // Return the updated form data
      return {
        ...prevFormData,
        aidStations: updatedAidStations
      };
    });
  };

  const handleAddFinish = () => {
    const finishStation = {
      location: '',
      name: '',
      hasDropBag: false,
      items: [],
      hasPacer: false,
      pacerName: '',
      hasCrew: false,
      notes: '',
      isFinish: true
    };
    handleAddAidStation(finishStation);
  };

  return (
    <Paper
      component="form"
      onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}
      elevation={2}
      sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 2 }, borderRadius: 2 }}
    >
      {/* Add a title that shows if we're editing */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {isEditing ? `Edit Race Plan: ${raceName}` : 'Create New Race Plan'}
        </Typography>
      </Box>

      {/* Header with Logo */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5 }}>
        <Box>
          <img 
            src="/ultrapackr.png" 
            alt="UltraPackr Logo" 
            style={{ 
              height: isMobile ? '100px' : '120px',
              maxWidth: '100%',
              objectFit: 'contain'
            }} 
          />
        </Box>
        <Typography 
          variant="subtitle1" 
          color="text.secondary" 
          textAlign="center"
          sx={{ mt: -1 }}
        >
          Race Day Planning, Perfected.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Desktop Stepper (horizontal) with visual progress */}
      {!isMobile && (
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper 
            alternativeLabel 
            activeStep={activeStep} 
            connector={<ColorlibConnector />}
            sx={{ bgcolor: 'transparent' }}
          >
            {steps.map((step, index) => {
              const isClickable = isStepAccessible(index);
              
              return (
                <Step 
                  key={step.label} 
                  completed={completedSteps[index]}
                  sx={{ cursor: isClickable ? 'pointer' : 'default' }}
                  onClick={() => isClickable && handleStepClick(index)}
                >
                  <StepLabel 
                    StepIconComponent={ColorlibStepIcon}
                    sx={{
                      '& .MuiStepLabel-label': {
                        mt: 1,
                        fontWeight: index === activeStep ? 700 : 500,
                        color: index === activeStep ? 'primary.main' : 'text.secondary',
                        fontSize: '0.875rem',
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                      '& .MuiStepLabel-label.Mui-disabled': {
                        color: 'text.disabled',
                      },
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      )}

      {/* Mobile Stepper (text only) with clickable navigation */}
      {isMobile && (
        <Box sx={{ mb: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {steps.map((step, index) => {
              const isCompleted = completedSteps[index];
              const isActive = index === activeStep;
              const isClickable = isStepAccessible(index);
              
              return (
                <Button
                  key={index}
                  variant="outlined"
                  disableElevation
                  onClick={() => isClickable && handleStepClick(index)}
                  disabled={!isClickable}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    borderColor: isActive ? 'primary.main' : 'divider',
                    bgcolor: isActive ? 'background.paper' : 'rgba(0, 0, 0, 0.02)',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive ? 'bold' : 'normal',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: isActive ? 'background.paper' : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      bgcolor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  {index + 1}. {step.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Content area with grey background */}
      <Box 
        sx={{ 
          width: '100%',
          bgcolor: 'rgba(0, 0, 0, 0.05)', 
          borderRadius: 1,
          p: 3,
          mt: 0,
          mb: 4
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {steps[activeStep].label}
        </Typography>
        {getStepContent(activeStep)}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Navigation Controls - Hidden (using floating buttons instead) */}
      <Box sx={{ display: 'none' }}>
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<KeyboardArrowLeft />}
        >
          Back
        </Button>
        
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              type="submit"
              onClick={() => setGenerateButtonClicked(true)}
              sx={{
                bgcolor: 'accent.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'accent.dark',
                },
              }}
            >
              Generate Race Plan
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<KeyboardArrowRight />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Mobile Progress Indicator */}
      {isMobile && (
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{ mt: 3, bgcolor: 'transparent' }}
          nextButton={<div />}
          backButton={<div />}
        />
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          ULTRAPACKR.COM
        </Typography>
      </Box>

      {/* Floating Navigation Buttons (bottom-right) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 1000,
        }}
      >
        {/* Previous Button - Hidden on mobile */}
        {activeStep > 0 && (
          <Button
            variant="contained"
            onClick={handleBack}
            startIcon={<KeyboardArrowLeft />}
            sx={{
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'action.hover',
                boxShadow: 6,
              },
              minWidth: { xs: 100, sm: 120 },
              display: { xs: 'none', sm: 'flex' }, // Hide on mobile
            }}
          >
            Previous
          </Button>
        )}
        
        {/* Next/Generate Button */}
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => {
              setGenerateButtonClicked(true);
              handleSubmit(new Event('submit'), true);
            }}
            sx={{
              bgcolor: 'accent.main',
              color: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'accent.dark',
                boxShadow: 6,
              },
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 48, sm: 'auto' }, // Touch-friendly on mobile
              fontSize: { xs: '0.9rem', sm: '0.875rem' },
            }}
          >
            Generate
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<KeyboardArrowRight />}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: 6,
              },
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 48, sm: 'auto' }, // Touch-friendly on mobile
              fontSize: { xs: '0.9rem', sm: '0.875rem' },
            }}
          >
            Next
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default RaceForm; 